// quotations.js — 단가마스터/견적산출 앱(price-manager-49b39)에 저장된 "견적서 작성내역"을
// 읽기 전용으로 가져와 주간 경영관리 리포트에 표시하기 위한 모듈.
//
// ⚠️ 재고 앱에서는 단가마스터 앱의 견적 컬렉션 이름을 알 수 없어서, 흔히 쓰이는 후보 이름을 순서대로
// 조회해보고 문서가 실제로 들어있는 첫 번째 컬렉션을 사용한다(권한 오류·빈 컬렉션은 조용히 건너뜀).
// 정확한 컬렉션 이름을 알게 되면 CANDIDATE_COLLECTIONS 맨 앞에 그 이름만 넣어주면 된다.
// 필드 이름도 앱마다 달라서, 날짜/거래처/금액/문서번호를 후보 키 목록으로 탐색해 정규화한다.
import { getFirestore, collection, getDocs, query, limit } from "firebase/firestore";
import { priceManagerApp, ensureSignedIn } from "./ecountSales";

const db = () => getFirestore(priceManagerApp());

const CANDIDATE_COLLECTIONS = [
  "quotations", "quotes", "quoteHistory", "savedQuotes", "estimates",
  "quotationHistory", "quoteDocs", "견적서", "견적",
];

const DATE_KEYS = ["quoteDate", "date", "issueDate", "createdAt", "savedAt", "updatedAt", "작성일", "작성일자"];
const CUSTOMER_KEYS = ["customer", "customerName", "client", "clientName", "company", "vendor", "거래처", "고객사"];
const AMOUNT_KEYS = ["totalAmount", "total", "grandTotal", "totAmt", "amount", "sum", "합계", "총액"];
const NO_KEYS = ["quoteNo", "no", "docNo", "number", "title", "name", "견적번호"];

const toDateStr = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v.slice(0, 10);
  if (v.toDate) { try { return v.toDate().toISOString().slice(0, 10); } catch { return ""; } }
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === "object" && v.seconds) return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  return "";
};
const pick = (obj, keys, fn) => {
  for (const k of keys) {
    if (obj[k] === undefined || obj[k] === null || obj[k] === "") continue;
    const v = fn ? fn(obj[k]) : obj[k];
    if (v !== "" && v !== null && v !== undefined) return v;
  }
  return "";
};

function normalize(id, x) {
  const rows = Array.isArray(x.rows) ? x.rows : Array.isArray(x.items) ? x.items : [];
  const amountRaw = pick(x, AMOUNT_KEYS);
  const amount = Number(String(amountRaw).replace(/,/g, "")) || rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  return {
    id,
    no: String(pick(x, NO_KEYS) || id),
    date: pick(x, DATE_KEYS, toDateStr),
    customer: String(pick(x, CUSTOMER_KEYS) || "—"),
    amount,
    itemCount: rows.length,
    status: String(x.status || x.state || x.상태 || ""),
  };
}

// 견적서 작성내역 조회. 반환: { source: 사용한 컬렉션명(없으면 ""), list: [정규화 문서], error: "" }
export async function fetchQuotations() {
  try { await ensureSignedIn(); } catch (e) { return { source: "", list: [], error: e.message || String(e) }; }
  const database = db();
  for (const name of CANDIDATE_COLLECTIONS) {
    try {
      const snap = await getDocs(query(collection(database, name), limit(500)));
      if (snap.empty) continue;
      const list = snap.docs
        .map((d) => normalize(d.id, d.data() || {}))
        .filter((q) => q.date || q.amount)
        .sort((a, b) => (a.date < b.date ? 1 : -1));
      if (list.length) return { source: name, list, error: "" };
    } catch {
      // 권한 없음/컬렉션 없음 → 다음 후보로
    }
  }
  return { source: "", list: [], error: "" };
}

export function summarizeQuotations(list, { periodStart, periodEnd } = {}) {
  const all = list || [];
  const week = all.filter((q) => q.date && q.date >= periodStart && q.date <= periodEnd);
  const amt = (rows) => rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  return {
    week, weekCount: week.length, weekAmount: amt(week),
    totalCount: all.length, totalAmount: amt(all),
    customers: new Set(week.map((q) => q.customer)).size,
  };
}
